import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaFileAlt, FaExternalLinkAlt, FaOrcid } from 'react-icons/fa';
import styles from './Publications.module.css';

const Publications: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const publications = [
    {
      title: 'Intelligent Monitoring of Bolt Loosening Using Vibration Analysis and Deep Learning',
      journal: 'Measurement Science & Technology',
      year: '2025',
      status: 'Accepted',
      type: 'Journal'
    },
    {
      title: 'Acoustic Emission for Structural Diagnostics of AlSi10Mg by SLM',
      journal: 'Journal Submission',
      year: '2025',
      status: 'In Review',
      type: 'Journal'
    },
    {
      title: 'Hybrid Probabilistic Models for Uncertainty-Aware AE Classification',
      journal: 'International Conference',
      year: '2025',
      status: 'Published',
      type: 'Conference'
    },
    {
      title: 'Structural Health Monitoring of Self-Healing Composites',
      journal: 'Horizon Europe Deliverable',
      year: '2024',
      status: 'Published',
      type: 'Report'
    }
  ];

  const ORCID_URL = 'https://orcid.org/0000-0002-0216-7571';

  return (
    <section id="publications" className={styles.publications} ref={ref}>
      <div className={styles.container}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className={styles.header}
        >
          <h2 className={styles.title}>Publications</h2>
          <div className={styles.titleUnderline}></div>
        </motion.div>

        <div className={styles.publicationsList}>
          {publications.map((pub, index) => (
            <motion.div
              key={index}
              className={styles.publicationCard}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={styles.publicationIcon}>
                <FaFileAlt />
              </div>

              <div className={styles.publicationContent}>
                <div className={styles.publicationMeta}>
                  <span className={styles.publicationType}>{pub.type}</span>
                  <span className={styles.publicationYear}>{pub.year}</span>
                  <span className={`${styles.publicationStatus} ${styles[pub.status.toLowerCase().replace(' ', '')]}`}>
                    {pub.status}
                  </span>
                </div>

                <h3 className={styles.publicationTitle}>{pub.title}</h3>
                <p className={styles.publicationJournal}>{pub.journal}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className={styles.orcidSection}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className={styles.orcidContent}>
            <FaOrcid className={styles.orcidLogo} />
            <div className={styles.orcidText}>
              <h3>View Full Publication List</h3>
              <p>Access my complete research portfolio on ORCID</p>
            </div>
          </div>
          <a 
            href={ORCID_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.orcidButton}
          >
            <span>Open ORCID Profile</span>
            <FaExternalLinkAlt />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Publications;
